// src/ApiPanel/pages/gst/GstPayReport.jsx
// GST challans: the register, recording a new one, and approving it.
//
// The total is deliberately computed and shown but NOT sent — the backend
// recalculates it from the heads and ignores any total in the body. Showing
// it live is a convenience so the person recording a challan can check it
// against the paper before saving; it is not the number of record.
import React, { useMemo, useState } from "react";
import {
  Badge, Card, DataTable, DateField, ExportButton, FilterBar, Modal, Notice,
  Pagination, ReportPage, SelectField, Stat, StatGrid, TextField, count,
  daysAgoISO, money, prettyDate, rupees, todayISO,
} from "../../components/reports/ReportKit";
import { useAction, useReportQuery } from "../../hooks/useReportQuery";
import { apiPanelDownload, qs } from "../../utils/apiPanelFetch";
import { API_PANEL_ENDPOINTS } from "../../constants/apiPanelAuth";
import { useApiPanelSession } from "../../context/ApiPanelSessionContext";

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "PAID", label: "Paid" },
  { value: "FAILED", label: "Failed" },
];

const MODE_OPTIONS = [
  { value: "NETBANKING", label: "Net banking" },
  { value: "NEFT", label: "NEFT" },
  { value: "RTGS", label: "RTGS" },
  { value: "OTC", label: "Over the counter" },
];

const HEADS = ["cgst", "sgst", "igst", "cess", "interest", "penalty", "fee"];

const defaults = () => ({
  start_date: daysAgoISO(179),
  end_date: todayISO(),
  gstin: "",
  pay_status: "",
  page: 1,
});

export default function GstPayReport() {
  const { user } = useApiPanelSession();
  const [filters, setFilters] = useState(defaults);
  const [applied, setApplied] = useState(defaults);
  const [exporting, setExporting] = useState(false);
  const [showRecord, setShowRecord] = useState(false);

  const { data, loading, error, reload } = useReportQuery(
    API_PANEL_ENDPOINTS.GST_PAY_REPORT,
    applied
  );
  const action = useAction();

  const totals = data?.totals || {};
  const rows = data?.data || [];
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  const handleExport = async () => {
    setExporting(true);
    try {
      await apiPanelDownload(
        `${API_PANEL_ENDPOINTS.GST_PAY_REPORT}${qs({ ...applied, export: "csv", page: undefined })}`,
        `gst_pay_${applied.start_date}_${applied.end_date}.csv`
      );
    } catch {
      /* list unaffected */
    } finally {
      setExporting(false);
    }
  };

  const approve = async (row) => {
    if (!window.confirm(`Mark challan ${row.challan_no} as paid? This cannot be undone here.`)) return;
    const res = await action.run(
      API_PANEL_ENDPOINTS.GST_PAY_ACTION,
      { action: "approve", id: row.id },
      { form: true }
    );
    if (res) reload();
  };

  return (
    <ReportPage
      title="GST PAY Report"
      subtitle="Challans paid against each tax period, and the ones still waiting for approval."
      actions={
        <>
          <button type="button" className="apip-btn apip-btn-primary" onClick={() => setShowRecord(true)}>
            <i className="iconoir-plus" /> Record Challan
          </button>
          <ExportButton onExport={handleExport} busy={exporting} />
        </>
      }
    >
      <Card>
        <FilterBar
          onApply={() => setApplied({ ...filters, page: 1 })}
          onReset={() => {
            const fresh = defaults();
            setFilters(fresh);
            setApplied(fresh);
          }}
          busy={loading}
        >
          <DateField label="From" value={filters.start_date} onChange={set("start_date")} max={filters.end_date} />
          <DateField label="To" value={filters.end_date} onChange={set("end_date")} min={filters.start_date} max={todayISO()} />
          <TextField label="GSTIN" value={filters.gstin} onChange={(v) => set("gstin")(v.toUpperCase())} maxLength={15} />
          <SelectField label="Status" value={filters.pay_status} onChange={set("pay_status")} options={STATUS_OPTIONS} />
        </FilterBar>
      </Card>

      <StatGrid>
        <Stat label="Challans" value={count(totals.payments)} />
        <Stat label="CGST" value={rupees(totals.cgst)} />
        <Stat label="SGST" value={rupees(totals.sgst)} />
        <Stat label="IGST" value={rupees(totals.igst)} />
        <Stat label="Interest + Penalty" value={rupees(Number(totals.interest || 0) + Number(totals.penalty || 0))} tone="warn" />
        <Stat label="Total Paid" value={rupees(totals.total)} tone="brand" />
      </StatGrid>

      {action.message && <Notice tone="ok">{action.message}</Notice>}
      {action.error && <Notice tone="bad">{action.error}</Notice>}

      <Card title="Challans">
        <DataTable
          columns={[
            { key: "challan_no", label: "CPIN", render: (r) => <span className="apip-mono">{r.challan_no}</span> },
            { key: "period", label: "Period" },
            { key: "gstin", label: "GSTIN", render: (r) => <span className="apip-mono">{r.gstin}</span> },
            { key: "payment_date", label: "Paid On", render: (r) => prettyDate(r.payment_date) },
            { key: "mode", label: "Mode", render: (r) => r.mode || "—" },
            { key: "cgst", label: "CGST", numeric: true, render: (r) => money(r.cgst) },
            { key: "sgst", label: "SGST", numeric: true, render: (r) => money(r.sgst) },
            { key: "igst", label: "IGST", numeric: true, render: (r) => money(r.igst) },
            { key: "total", label: "Total", numeric: true, render: (r) => <strong>{money(r.total)}</strong> },
            { key: "status", label: "Status", render: (r) => <Badge value={r.status} /> },
            {
              key: "actions",
              label: "",
              width: 130,
              render: (r) => {
                if (r.status !== "PENDING") return null;
                // The backend refuses self-approval; hiding the button here
                // just avoids offering an action that will be rejected.
                if (r.created_by && user?.name && r.created_by === user.name) {
                  return <span className="apip-hint">Awaiting another approver</span>;
                }
                return (
                  <button type="button" className="apip-btn apip-btn-sm apip-btn-ghost" disabled={action.busy} onClick={() => approve(r)}>
                    Approve
                  </button>
                );
              },
            },
          ]}
          rows={rows}
          loading={loading}
          error={error}
          empty="No challans recorded for these filters."
          rowKey={(r) => r.id}
        />
        <Pagination meta={data?.pagination} onPage={(page) => setApplied((a) => ({ ...a, page }))} />
      </Card>

      <RecordChallanModal
        open={showRecord}
        onClose={() => setShowRecord(false)}
        onDone={() => {
          setShowRecord(false);
          reload();
        }}
      />
    </ReportPage>
  );
}

function RecordChallanModal({ open, onClose, onDone }) {
  const action = useAction();
  const [form, setForm] = useState({
    gstin: "", period: currentMonth(), challan_no: "", bank_ref: "",
    payment_date: todayISO(), mode: "NETBANKING", remarks: "",
    cgst: "", sgst: "", igst: "", cess: "", interest: "", penalty: "", fee: "",
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const total = useMemo(
    () => HEADS.reduce((acc, h) => acc + (Number(form[h]) || 0), 0),
    [form]
  );

  const submit = async (e) => {
    e.preventDefault();
    const res = await action.run(
      API_PANEL_ENDPOINTS.GST_PAY_ACTION,
      { action: "create", ...form },
      { form: true }
    );
    if (res) onDone();
  };

  return (
    <Modal
      open={open}
      title="Record a GST challan"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="apip-btn apip-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" form="apip-challan-form" className="apip-btn apip-btn-primary" disabled={action.busy || total <= 0}>
            {action.busy ? "Saving…" : "Save as Pending"}
          </button>
        </>
      }
    >
      <form id="apip-challan-form" onSubmit={submit}>
        <Notice tone="info">
          Saving records the challan as <strong>Pending</strong>. Someone else has to approve it
          before it counts as paid — the person who records a challan cannot approve their own.
        </Notice>

        <div className="apip-form-grid">
          <label className="apip-field">
            <span>GSTIN</span>
            <input type="text" className="apip-input apip-mono" required maxLength={15} value={form.gstin}
              onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))} placeholder="27AAAAA0000A1Z5" />
          </label>
          <label className="apip-field">
            <span>Tax period</span>
            <input type="month" className="apip-input" required value={form.period} onChange={set("period")} max={currentMonth()} />
          </label>
          <label className="apip-field">
            <span>Challan no (CPIN)</span>
            <input type="text" className="apip-input apip-mono" required maxLength={20} pattern="[A-Za-z0-9-]{6,20}" value={form.challan_no} onChange={set("challan_no")} />
          </label>
          <label className="apip-field">
            <span>Bank ref (CIN)</span>
            <input type="text" className="apip-input apip-mono" maxLength={30} value={form.bank_ref} onChange={set("bank_ref")} />
          </label>
          <label className="apip-field">
            <span>Payment date</span>
            <input type="date" className="apip-input" required max={todayISO()} value={form.payment_date} onChange={set("payment_date")} />
          </label>
          <label className="apip-field">
            <span>Mode</span>
            <select className="apip-input" value={form.mode} onChange={set("mode")}>
              {MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        </div>

        <h4 className="apip-form-subhead">Amounts</h4>
        <div className="apip-form-grid apip-form-grid-4">
          {HEADS.map((head) => (
            <label className="apip-field" key={head}>
              <span>{head.toUpperCase()}</span>
              <input type="number" className="apip-input" min="0" step="0.01" value={form[head]} onChange={set(head)} placeholder="0.00" />
            </label>
          ))}
        </div>

        <div className="apip-computed">
          <span>Challan total</span>
          <strong>{rupees(total.toFixed(2))}</strong>
          <small>computed server-side too — a total sent from here is ignored</small>
        </div>

        <label className="apip-field">
          <span>Remarks</span>
          <textarea className="apip-input" rows={2} maxLength={250} value={form.remarks} onChange={set("remarks")} />
        </label>

        {action.error && <Notice tone="bad">{action.error}</Notice>}
      </form>
    </Modal>
  );
}

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}
